import type { FleetMetricsRow, FleetWindowMetrics } from "./types";

/**
 * The census metric derivations — ONE home, shared by the fleet table's
 * columns and the hotel page's metric tiles. The tones encode census's own
 * readings (a zero where the hotel's own rate expected several; value paired
 * with bookings to catch valueless recording; the paid capture on Rule 3's own
 * thresholds), so the two surfaces cannot call the same number different names.
 */

export type WindowMode = "recent" | "full";

export type Tone = "" | "zero" | "warn" | "alert";

export const TONE_CLASS: Record<Tone, string> = {
  "": "text-content-primary",
  zero: "text-gray-400",
  warn: "text-warning-dark font-semibold",
  alert: "text-danger-dark font-semibold",
};

/**
 * The metric hints the fleet table and the hotel tiles both show. Each was
 * written out twice, once per surface, and had already drifted in
 * capitalisation — the cell and the tile explain the same number, so they
 * say it in one place.
 */
export const CONV_RATE_HINT = "purchase conversions / Google Ads clicks";

export const PAID_CAPTURE_HINT =
  "GA4 paid (google / cpc) sessions / Ads clicks, capped at 100% — Rule 3 fails under 40% once the window has 100+ clicks";

/** The expected-bookings hint, or nothing when the baseline expects under one. */
export function expectedBookingsHint(
  expected: number | null | undefined,
): string | undefined {
  if (expected == null || expected < 1) return undefined;
  return `~${Math.round(expected)} expected from this hotel's own conversion rate`;
}

export function windowOf(
  metrics: FleetMetricsRow | undefined,
  mode: WindowMode,
): FleetWindowMetrics | undefined {
  return mode === "recent" ? metrics?.recent : metrics?.full;
}

export function ratio(
  num: number | null | undefined,
  den: number | null | undefined,
): number | null {
  if (num == null || den == null || den <= 0) return null;
  return num / den;
}

/** Paid-session capture, capped at 100% (census: GA4 restates > clicks). */
export function capture(
  paid: number | null | undefined,
  clicks: number | null | undefined,
): number | null {
  const r = ratio(paid, clicks);
  return r == null ? null : Math.min(r, 1);
}

/**
 * Bookings the hotel's OWN 90-day conversion rate expected in the recent
 * window — what separates "a quiet fortnight" from "the tag stopped firing".
 * Null outside the recent window (the 90-day figure is its own baseline).
 */
export function expectedBookings(
  metrics: FleetMetricsRow | undefined,
  mode: WindowMode,
): number | null {
  if (mode !== "recent" || !metrics) return null;
  const rate = ratio(metrics.full.conversions, metrics.full.clicks);
  if (rate == null || metrics.recent.clicks == null) return null;
  return metrics.recent.clicks * rate;
}

export function bookingsTone(
  main: FleetWindowMetrics | undefined,
  expected: number | null,
): Tone {
  if (main?.conversions == null) return "";
  if (Math.round(main.conversions) !== 0) return "";
  if (expected != null && expected >= 3) return "alert";
  if (expected != null && expected >= 1) return "warn";
  return "zero";
}

export function valueTone(main: FleetWindowMetrics | undefined): Tone {
  if (main?.conversionsValue == null) return "";
  if (
    main.conversions != null &&
    main.conversions > 0 &&
    main.conversionsValue / main.conversions < 10
  )
    return "alert";
  return Math.round(main.conversionsValue) === 0 ? "zero" : "";
}

/** Rule 3's own thresholds: fails under 40% once the window has 100+ clicks. */
export function paidCaptureTone(main: FleetWindowMetrics | undefined): Tone {
  const paid = capture(main?.ga4PaidSessions, main?.clicks);
  if (paid != null && (main?.clicks ?? 0) >= 100 && paid < 0.4) return "alert";
  return paid == null ? "zero" : "";
}
